import { useState } from 'react';
import type { ReactElement } from 'react';
import { isoDateTime, paisa } from '@fleet/shared';
import { useBookingsQuery } from '@/api/endpoints';
import type { Booking } from '@/api/types';
import { LinkButton } from '@/design/Button';
import { Identifier, MoneyText } from '@/design/Data';
import { DataTable } from '@/design/DataTable';
import type { Column } from '@/design/DataTable';
import { Field, SelectInput, TextInput } from '@/design/Field';
import { Icon } from '@/design/icons';
import { Panel } from '@/design/Panel';
import { StatusPill } from '@/design/StatusPill';
import { useTime } from '@/lib/time';
import { bookingStatus } from './status';

const SORTS = [
  { value: 'recent', label: 'Recently taken' },
  { value: 'soonest', label: 'Going out soonest' },
  { value: 'latest', label: 'Going out latest' },
] as const;

/** Every booking, with the filters and ordering the counter actually uses. */
export function BookingsPage(): ReactElement {
  const [filter, setFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<string>('recent');
  const { data, isLoading, isError, refetch } = useBookingsQuery({
    status: filter,
    q: search.trim(),
    sort,
  });
  const { formatDateTime } = useTime();

  const filters = [
    { value: '', label: 'All' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'on_trip', label: 'On trip' },
    { value: 'returned', label: 'Returned' },
    { value: 'closed', label: 'Closed' },
    { value: 'cancelled,no_show', label: 'Cancelled' },
  ];

  const columns: readonly Column<Booking>[] = [
    { key: 'reference', header: 'Booking', render: (row) => <Identifier>{row.reference}</Identifier> },
    { key: 'customer', header: 'Customer', render: (row) => row.customer_name },
    {
      key: 'vehicle',
      header: 'Vehicle',
      render: (row) =>
        row.vehicle_name === null || row.vehicle_name === undefined ? (
          <span className="text-attention">{row.category || 'Not assigned'}</span>
        ) : (
          row.vehicle_name
        ),
    },
    {
      key: 'out',
      header: 'Out',
      render: (row) => (
        <span className="tnum">
          {formatDateTime(isoDateTime(row.start_at))}
        </span>
      ),
    },
    {
      key: 'back',
      header: 'In',
      render: (row) => (
        <span className="tnum">
          {formatDateTime(isoDateTime(row.end_at))}
        </span>
      ),
    },
    {
      key: 'total',
      header: 'Total',
      numeric: true,
      render: (row) => <MoneyText amount={paisa(row.total ?? 0)} />,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusPill status={bookingStatus(row.status)} />,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* The one New booking button in the app.
          It lived in the header for a while, on every screen at once, which
          put it beside Sign out on a shared counter machine and repeated it
          here. One control, on the screen the bookings are on. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-display font-semibold">Bookings</h1>
        <LinkButton to="/bookings/new" intent="primary" size="touch">
          <Icon name="plus" width="16" height="16" />
          New booking
        </LinkButton>
      </div>

      <Panel
        title="All bookings"
        note={
          data === undefined
            ? SORTS.find((option) => option.value === sort)?.label
            : `${String(data.count)} ${data.count === 1 ? 'booking' : 'bookings'}, ${(
                SORTS.find((option) => option.value === sort)?.label ?? ''
              ).toLowerCase()}`
        }
        current
        actions={
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-44">
              {/*
                This list was ordered by trip date under a heading that said
                "newest first", which are two different things: a booking taken
                this morning for next Dashain sorted above one taken this
                morning for tomorrow, so the booking somebody had just saved
                was almost never at the top.
              */}
              <Field label="Order">
                {({ id, describedBy }) => (
                  <SelectInput
                    id={id}
                    describedBy={describedBy}
                    value={sort}
                    onChange={setSort}
                    options={SORTS}
                  />
                )}
              </Field>
            </div>
            <div className="w-56">
              <Field label="Search">
                {({ id, describedBy }) => (
                  <TextInput
                    id={id}
                    describedBy={describedBy}
                    value={search}
                    onChange={setSearch}
                  />
                )}
              </Field>
            </div>
          </div>
        }
      >
        <div className="flex flex-wrap gap-1 border-b border-rule px-4 py-2">
          {filters.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setFilter(option.value)}
              className={[
                'rounded-sm px-2.5 py-1 text-base',
                filter === option.value
                  ? 'bg-signal-wash font-semibold text-signal'
                  : 'text-ink-muted hover:bg-paper hover:text-ink',
              ].join(' ')}
            >
              {option.label}
            </button>
          ))}
        </div>

        <DataTable
          columns={columns}
          rows={data?.results ?? []}
          rowKey={(row) => row.id}
          rowHref={(row) => `/bookings/${row.id}`}
          isLoading={isLoading}
          isError={isError}
          onRetry={() => void refetch()}
          errorHeadline="The bookings did not load"
          errorDetail="The server did not answer. Check the connection and load them again."
          empty={{
            headline: search === '' && filter === '' ? 'No bookings yet' : 'Nothing matches',
            detail:
              search === '' && filter === ''
                ? 'A booking needs a customer, a window, and a vehicle or a category. Everything else can be added before handover.'
                : 'Try a different search, or clear the filter.',
            action: (
              <LinkButton to="/bookings/new" intent="primary">
                <Icon name="plus" width="16" height="16" />
                New booking
              </LinkButton>
            ),
          }}
        />
      </Panel>
    </div>
  );
}
